import { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, ExternalLink, Send } from "lucide-react";
import { restaurantInfo } from "../mock";
import SectionHeading from "../components/SectionHeading";
import SEO, { restaurantSchema, breadcrumbSchema } from "../components/SEO";
import YelpIcon from "../components/YelpIcon";
import EzLocalIcon from "../components/EzLocalIcon";
import Captcha from "../components/Captcha";
import VisitSection from "../components/VisitSection";
import { useToast } from "../hooks/use-toast";
import { useT, useLang } from "../context/LanguageContext";
import { CONTACT_STRINGS, SEO_STRINGS, NAV_STRINGS } from "../i18n/strings";
import { trackEvent } from "../utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const t = useT();
  const { lang } = useLang();
  const isEs = lang === "es";
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const [captcha, setCaptcha] = useState({ answer: "", token: "" });
  const [captchaKey, setCaptchaKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const F = CONTACT_STRINGS.form;
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({
        title: t(F.requiredTitle.en, F.requiredTitle.es),
        description: t(F.requiredDesc.en, F.requiredDesc.es),
        variant: "destructive",
      });
      return;
    }
    if (!captcha.answer || !captcha.token) {
      toast({
        title: t(F.captchaTitle.en, F.captchaTitle.es),
        description: t(F.captchaDesc.en, F.captchaDesc.es),
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        website: form.website, // honeypot
        captcha_answer: captcha.answer,
        captcha_token: captcha.token,
      });
      toast({
        title: t(F.successTitle.en, F.successTitle.es),
        description: res.data?.message || t(F.successDesc.en, F.successDesc.es),
      });
      trackEvent("contact_form_submit", { status: "success" });
      trackEvent("form_submit", { form_id: "contact", form_name: "Contact form" });
      setForm({ name: "", email: "", phone: "", message: "", website: "" });
      setCaptcha({ answer: "", token: "" });
      setCaptchaKey((k) => k + 1);
    } catch (err) {
      const status = err?.response?.status;
      let detail =
        err?.response?.data?.detail || t(F.error.en, F.error.es);
      if (status === 429) {
        detail = t(F.rateLimit.en, F.rateLimit.es);
      }
      toast({
        title: t(F.errorTitle.en, F.errorTitle.es),
        description: detail,
        variant: "destructive",
      });
      setCaptcha({ answer: "", token: "" });
      setCaptchaKey((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title={SEO_STRINGS.contact.title.en}
        titleEs={SEO_STRINGS.contact.title.es}
        description={SEO_STRINGS.contact.description.en}
        descriptionEs={SEO_STRINGS.contact.description.es}
        path="/contact"
        keywords="Nina's Taqueria contact, Los Gatos Mexican restaurant address, taqueria phone number Los Gatos, Mexican restaurant hours"
        keywordsEs="contacto Nina's Taqueria, dirección restaurante mexicano Los Gatos, teléfono taquería Los Gatos, horario restaurante mexicano"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            restaurantSchema,
            breadcrumbSchema([
              { name: isEs ? "Inicio" : "Home", path: "/" },
              { name: isEs ? "Contacto" : "Contact", path: "/contact" },
            ]),
          ],
        }}
      />
      <main className="pt-24 bg-cream-50">
      {/* Hero */}
      <section className="relative min-h-[45vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.wixstatic.com/media/973942_754234f476dd4923a7bc11eaed6119d3~mv2.jpg/v1/fill/w_1920,h_1080,al_c,q_90,enc_avif,quality_auto/tortilla-soup.jpg"
            alt="Ancestral tortilla soup at Nina's Taqueria — visit us in Los Gatos"
            className="w-full h-full object-cover ken-burns"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-granite-dark/85 via-granite-dark/50 to-granite-dark/15" />
          <div className="absolute inset-0 bg-gradient-to-t from-granite-dark/50 via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 text-cream">
          <div className="font-hand text-3xl text-brand-light mb-2">{t(CONTACT_STRINGS.eyebrow.en, CONTACT_STRINGS.eyebrow.es)}</div>
          <h1 className="font-display text-5xl md:text-7xl font-semibold leading-[1.02]">{t(CONTACT_STRINGS.title.en, CONTACT_STRINGS.title.es)}</h1>
          <p className="mt-4 max-w-2xl text-cream/90 text-lg">
            {t(CONTACT_STRINGS.sub.en, CONTACT_STRINGS.sub.es)}
          </p>
        </div>
      </section>

      {/* Info + Form */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Info */}
          <div>
            <SectionHeading
              align="left"
              eyebrow={t(CONTACT_STRINGS.visitEyebrow.en, CONTACT_STRINGS.visitEyebrow.es)}
              title={t(CONTACT_STRINGS.visitTitle.en, CONTACT_STRINGS.visitTitle.es)}
              subtitle={t(CONTACT_STRINGS.visitSub.en, CONTACT_STRINGS.visitSub.es)}
            />
            <div className="space-y-4">
              <InfoRow icon={MapPin} label={t(CONTACT_STRINGS.labelAddress.en, CONTACT_STRINGS.labelAddress.es)} value={restaurantInfo.location.full} href={`https://maps.google.com/?q=${encodeURIComponent(restaurantInfo.location.full)}`} />
              <InfoRow icon={Phone} label={t(CONTACT_STRINGS.labelPhone.en, CONTACT_STRINGS.labelPhone.es)} value={restaurantInfo.phone} href={`tel:${restaurantInfo.phone}`} />
              <InfoRow icon={Mail} label={t(CONTACT_STRINGS.labelEmail.en, CONTACT_STRINGS.labelEmail.es)} value={restaurantInfo.email} href={`mailto:${restaurantInfo.email}`} />
            </div>

            <div className="mt-8 p-6 bg-cream rounded-2xl border border-cream-100">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-brand" />
                <h3 className="font-display text-xl font-semibold text-granite-dark">{t(CONTACT_STRINGS.hoursTitle.en, CONTACT_STRINGS.hoursTitle.es)}</h3>
              </div>
              <div className="text-center py-4 border-y border-dashed border-granite/10">
                <div className="font-display text-2xl text-granite-dark font-semibold">{t(CONTACT_STRINGS.openDaily.en, CONTACT_STRINGS.openDaily.es)}</div>
                <div className="text-granite/70 mt-1">{t(CONTACT_STRINGS.hoursValue.en, CONTACT_STRINGS.hoursValue.es)}</div>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <a href={restaurantInfo.socials.instagram} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-granite/20 flex items-center justify-center hover:bg-brand hover:border-brand hover:text-cream transition-colors" aria-label="Instagram">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={restaurantInfo.socials.facebook} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-granite/20 flex items-center justify-center hover:bg-brand hover:border-brand hover:text-cream transition-colors" aria-label="Facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={restaurantInfo.socials.yelp} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-granite/20 flex items-center justify-center hover:bg-[#d32323] hover:border-[#d32323] hover:text-cream transition-colors" aria-label="Yelp">
                <YelpIcon className="w-4 h-4" />
              </a>
              <a href={restaurantInfo.socials.ezlocal} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-granite/20 flex items-center justify-center hover:bg-brand hover:border-brand hover:text-cream transition-colors" aria-label="ezlocal">
                <EzLocalIcon className="w-4 h-4" />
              </a>
              <a href={restaurantInfo.externalLinks.orderOnline} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 ml-auto bg-brand text-cream px-5 py-2.5 rounded-full text-sm font-medium hover:bg-brand-dark transition-colors">
                {t(NAV_STRINGS.orderOnline.en, NAV_STRINGS.orderOnline.es)} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Form */}
          <div className="bg-cream p-8 md:p-10 rounded-3xl border border-cream-100 shadow-sm">
            <div className="font-hand text-2xl text-brand mb-1">{t(F.handTitle.en, F.handTitle.es)}</div>
            <h3 className="font-display text-3xl font-semibold text-granite-dark mb-6">{t(F.title.en, F.title.es)}</h3>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t(F.name.en, F.name.es)} name="name" value={form.name} onChange={handleChange} />
                <Field label={t(F.email.en, F.email.es)} name="email" type="email" value={form.email} onChange={handleChange} />
              </div>
              <Field label={t(F.phone.en, F.phone.es)} name="phone" value={form.phone} onChange={handleChange} />
              <div>
                <label className="text-xs uppercase tracking-widest text-granite/60 font-semibold">{t(F.message.en, F.message.es)}</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={5}
                  className="mt-1.5 w-full px-4 py-3 rounded-xl bg-cream-50 border border-cream-100 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm text-granite-dark transition-all resize-none"
                  placeholder={t(F.placeholder.en, F.placeholder.es)}
                />
              </div>

              {/* Honeypot — hidden from real users */}
              <div className="hidden" aria-hidden="true">
                <label>Website (leave empty)</label>
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={handleChange}
                />
              </div>

              <Captcha value={captcha} onChange={setCaptcha} refreshKey={captchaKey} />

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-granite-dark hover:bg-brand text-cream px-7 py-3.5 rounded-full font-medium text-sm transition-all shadow-md hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {submitting ? t(F.sending.en, F.sending.es) : (<>{t(F.send.en, F.send.es)} <Send className="w-4 h-4" /></>)}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Unified Visit section — map + reviews + hours + CTAs */}
      <VisitSection compact />
      </main>
    </>
  );
}

function Field({ label, name, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-granite/60 font-semibold">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        autoComplete={name === "email" ? "email" : name === "phone" ? "tel" : "on"}
        className="mt-1.5 w-full px-4 py-3 rounded-xl bg-cream-50 border border-cream-100 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm text-granite-dark transition-all"
      />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, href }) {
  const content = (
    <>
      <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center text-brand shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-widest text-granite/50 font-semibold">{label}</div>
        <div className="font-display text-lg text-granite-dark truncate">{value}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="flex items-center gap-4 group hover:text-brand transition-colors">
      {content}
    </a>
  ) : (
    <div className="flex items-center gap-4">{content}</div>
  );
}
